import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { apiClient } from '@/lib/api-client';
import DOMPurify from 'dompurify';

interface PrivacyPolicySection {
  id?: string;
  _id?: string;
  title: string;
  content: string;
}

export default function PrivacyPolicyPage() {
  const [sections, setSections] = useState<PrivacyPolicySection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bgImage, setBgImage] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        // data usually returns an array or { items: [...] }
        const res: any = await apiClient.getPrivacyPolicies();
        const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);

        try {
          const settings = await apiClient.getSectionSettings('privacy_policy_settings');
          const img = settings?.config?.backgroundImage || settings?.backgroundImage;
          if (mounted && img) {
            setBgImage(img);
          }
        } catch (e) {
          // ignore settings error
        }

        if (mounted) {
          setSections(list);
          setLoading(false);
        }
      } catch (err: any) {
        if (mounted) {
          console.error("Failed to load privacy policies", err);
          setError("Failed to load privacy policy content.");
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading privacy policy...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {bgImage && (
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.15
          }}
        />
      )}
      <div className="max-w-4xl mx-auto px-4 py-12 relative z-10">
        <Card className="bg-white/95 backdrop-blur-sm shadow-sm">
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {sections.length > 0 ? (
              sections.map((section, index) => (
                <div key={section.id || section._id || index} className="privacy-section">
                  <h2 className="text-xl font-semibold text-gray-800 mb-3">
                    {section.title}
                  </h2>
                  <div
                    className="prose prose-gray max-w-none text-gray-600"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
                  />
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500">
                {error || "No privacy policy content available at the moment."}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
